import React from 'react'
import PropTypes from 'prop-types'

/**
 * DeleteConfirm component renders a confirmation modal before deleting an item.
 * It includes cancel and delete buttons, with a loading state during the delete process.
 *
 * @module form/DeleteConfirm
 * @component
 * @param {Object} props - Component properties.
 * @param {Function} props.setDeleteId - Function to reset the ID of the item to be deleted.
 * @param {Function} props.setDeleteConfirm - Function to toggle the delete confirmation modal.
 * @param {Object} props.deleteCallMutation - Mutation object containing the loading state of the delete operation.
 * @param {boolean} props.deleteCallMutation.isLoading - Indicates if the delete operation is in progress.
 * @param {Function} props.onDelete - Function to execute the delete operation.
 *
 * @example
 * const [deleteId, setDeleteId] = useState(null);
 * const [deleteConfirm, setDeleteConfirm] = useState(false);
 * const deleteCallMutation = { isLoading: false };
 *
 * const handleDelete = () => {
 *   console.log('Deleting item...');
 * };
 *
 * <DeleteConfirm
 *   setDeleteId={setDeleteId}
 *   setDeleteConfirm={setDeleteConfirm}
 *   deleteCallMutation={deleteCallMutation}
 *   onDelete={handleDelete}
 * />
 *
 * @returns {JSX.Element} The DeleteConfirm modal component.
 */
export const DeleteConfirm = ({ setDeleteId, setDeleteConfirm, deleteCallMutation, onDelete }) => {
  return (
    <div
      id="removeItemModal"
      className="trtui-modal trtui-fade trtui-zoomIn trtui-show"
      tabIndex={-1}
      aria-modal="true"
      role="dialog"
      style={{
        display: 'block',
        backgroundColor: 'rgba(55,55,55,0.3)',
        position: 'fixed',
      }}
    >
      <div className="trtui-modal-dialog trtui-modal-dialog-centered">
        <div className="trtui-modal-content">
          <div className="trtui-modal-header">
            <button
              type="button"
              className="trtui-btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
              id="btn-close"
              onClick={() => {
                setDeleteConfirm(false)
                setDeleteId(null)
              }}
            />
          </div>
          <div className="trtui-modal-body">
            <div className="trtui-mt-2 trtui-text-center">
              <i className="bx bxs-trash bx-lg trtui-text-danger bx-tada"></i>
              <div className="trtui-mt-4 trtui-pt-2 trtui-fs-15 trtui-mx-4 trtui-mx-sm-5">
                <h4>Are you sure?</h4>
                <p className="text-muted mx-4 mb-0">Are you sure you want to delete this?</p>
              </div>
            </div>
            <div className="trtui-d-flex trtui-gap-2 trtui-justify-content-center trtui-mt-4 trtui-mb-2">
              <button
                type="button"
                className="trtui-btn trtui-w-sm trtui-btn-light"
                data-bs-dismiss="modal"
                onClick={() => {
                  setDeleteConfirm(false)
                  setDeleteId(null)
                }}
                disabled={deleteCallMutation.isLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="trtui-btn trtui-w-sm trtui-btn-danger"
                onClick={onDelete}
                disabled={deleteCallMutation.isLoading}
              >
                {deleteCallMutation.isLoading ? (
                  <span className="trtui-d-flex trtui-align-items-center">
                    <span className="trtui-flex-grow-1 trtui-me-2">Deleting...</span>
                    <span className="trtui-spinner-border trtui-flex-shrink-0" role="status">
                      <span className="trtui-visually-hidden">Deleting...</span>
                    </span>
                  </span>
                ) : (
                  'Yes, Delete It!'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

DeleteConfirm.propTypes = {
  /**
   * Function to reset the ID of the item to be deleted.
   */
  setDeleteId: PropTypes.func.isRequired,

  /**
   * Function to toggle the delete confirmation modal.
   */
  setDeleteConfirm: PropTypes.func.isRequired,

  /**
   * Mutation object containing the loading state of the delete operation.
   */
  deleteCallMutation: PropTypes.shape({
    isLoading: PropTypes.bool.isRequired,
  }).isRequired,

  /**
   * Function to execute the delete operation.
   */
  onDelete: PropTypes.func.isRequired,
}
